import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Switch,
  FormControlLabel, Button, Alert, CircularProgress, Stack, Chip,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/VpnKey';
import operatorAiKeysApi from '../../services/operatorAiKeysApi';
import { toServerDate } from '../../timezone';

// Mirrors the tenant AiProviderConfig provider enum.
const PROVIDERS = [
  'OPENAI', 'ANTHROPIC', 'GEMINI', 'BEDROCK', 'AZURE_OPENAI', 'MISTRAL',
  'COHERE', 'OLLAMA', 'OLLAMA_CLOUD', 'OPENAI_COMPATIBLE', 'OPENROUTER',
];

/**
 * Operator → AI Keys. A single platform-wide AI provider key. Tenants can opt
 * Vera / Sentinel / Workbooks onto this key (per feature) from their own
 * Settings → AI Providers page; when they don't, they bring their own key.
 */
export default function OperatorAiKeysPage() {
  const [form, setForm] = useState({ provider: 'OPENAI', apiKey: '', baseUrl: '', modelId: '', active: false });
  const [meta, setMeta] = useState({ apiKeySet: false, apiKeyMasked: null, updatedBy: null, updatedAt: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await operatorAiKeysApi.get();
      setForm((f) => ({
        ...f,
        provider: d?.provider || 'OPENAI',
        baseUrl: d?.baseUrl || '',
        modelId: d?.modelId || '',
        active: !!d?.active,
        apiKey: '', // never prefill the secret
      }));
      setMeta({
        apiKeySet: !!d?.apiKeySet, apiKeyMasked: d?.apiKeyMasked || null,
        updatedBy: d?.updatedBy || null, updatedAt: d?.updatedAt || null,
      });
    } catch (e) {
      setMsg({ severity: 'error', text: e.response?.data?.error || 'Failed to load operator AI key' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      // Blank apiKey => leave the stored key unchanged (backend contract).
      const payload = {
        provider: form.provider,
        baseUrl: form.baseUrl,
        modelId: form.modelId,
        active: form.active,
      };
      if (form.apiKey && form.apiKey.trim()) payload.apiKey = form.apiKey.trim();
      await operatorAiKeysApi.update(payload);
      setMsg({ severity: 'success', text: 'Operator AI key saved' });
      await load();
    } catch (e) {
      setMsg({ severity: 'error', text: e.response?.data?.error || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <KeyIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>AI Keys</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A single platform AI provider key. Tenants can switch Vera or Sentinel onto this key from their
        AI Providers settings; otherwise each tenant supplies its own key.
      </Typography>

      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

      <Card variant="outlined">
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField select label="Provider" value={form.provider} fullWidth size="small"
                  onChange={(e) => setForm({ ...form, provider: e.target.value })}>
                  {PROVIDERS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </TextField>
                <TextField label="Model id (optional)" value={form.modelId} fullWidth size="small"
                  onChange={(e) => setForm({ ...form, modelId: e.target.value })} />
              </Stack>
              <TextField label="Base URL (optional)" value={form.baseUrl} fullWidth size="small"
                onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
                helperText="For OpenAI-compatible / self-hosted endpoints." />
              <TextField
                label="API key" value={form.apiKey} fullWidth size="small" type="password"
                onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                placeholder={meta.apiKeySet ? `Stored: ${meta.apiKeyMasked || '••••'} — leave blank to keep` : 'Paste the provider API key'}
                helperText={meta.apiKeySet ? 'A key is stored (encrypted). Leave blank to keep it; type to replace.' : 'No key stored yet.'}
              />
              <FormControlLabel
                control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />}
                label="Active (tenants may use this key)"
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="contained" onClick={save} disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
                {meta.apiKeySet && <Chip size="small" label="Key stored" color="success" variant="outlined" />}
                {meta.updatedAt && (
                  <Typography variant="caption" color="text.secondary">
                    Updated {meta.updatedBy ? `by ${meta.updatedBy} ` : ''}· {toServerDate(meta.updatedAt)?.toLocaleString()}
                  </Typography>
                )}
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
